import _ from "lodash";

import {
  coursePostFragment,
  journalPostFragment,
  PostCommentFragment,
  messageCommentOfPostFragment,
  activityLikeCommentFragment,
  attachmentGroupsNodeFragmentForStudentAssignment,
} from "./PostFragments";
import {
  getCoursePostFeedQuery,
  getPostOfStudentQuery,
  getPostDetailQuery,
  getCourseIdForPostQuery,
  getCourseFilteredStudentQuery,
  getNodePostQuery,
  getParentJournalFeedQuery,
  getCourseUnitQuery,
  getCommentsOfPostQuery,
  getOrganizationAcademicYearsQuery,
  getPostsHavingTaggedGoalsQuery,
  getStudentCoursesQuery,
  getPlatformUserDetailsQuery,
  getLearningCourseStandardsQuery,
  getPlannerElementSetsOfCurriculumQuery,
  getCurriculumStandardsQuery,
} from "./PostQuery";

import { getStudentActivityCriteriaSetsQuery } from "./ActivityTrackingQuery";
import client from "lib/apolloClient/client";

export const getStudentJournalFeedFromCache = ({
  studentId,
  filters,
  uids,
  translationFilter,
  isDP,
  aclControlParameterFilter,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getPostOfStudentQuery,
      variables: {
        studentId,
        filters,
        ...(uids && { uids }),
        translationFilter,
        isDP,
        aclControlParameterFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCourseJournalFeedFromCache = ({
  courseId,
  filters,
  uids,
  translationFilter,
  isDP,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getCoursePostFeedQuery,
      variables: {
        id: courseId,
        filters,
        ...(uids && { uids }),
        translationFilter,
        isDP,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writeCourseJournalFeedToCache = ({
  courseId,
  filters,
  data,
  uids,
  translationFilter = {},
  isDP,
}) => {
  client.writeFragment({
    id: `Course:${courseId}`,
    fragment: coursePostFragment.coursePost,
    fragmentName: "courseItem",
    variables: {
      filters,
      ...(uids && { uids }),
      translationFilter,
      isDP,
    },
    data: data,
  });
};

export const getPostDetailFromFeedCache = ({ postId }) => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `Post:${postId}`,
      fragment: journalPostFragment.journalPost,
      fragmentName: "postItem",
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getPostDetailsFromCache = ({ postId, translationFilter = {} }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getPostDetailQuery,
      variables: {
        id: postId,
        translationFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCourseIdForPostFromCache = ({ postId }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getCourseIdForPostQuery,
      variables: {
        id: postId,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writePostDetailFragment = ({
  postId,
  data,
  fragmentName = "journalPost",
}) => {
  client.writeFragment({
    id: `Post:${postId}`,
    fragment: journalPostFragment[fragmentName],
    fragmentName: "postItem",
    data: data,
  });
};

export const getCourseFilteredStudentFromCache = ({
  courseId,
  filters,
  orderBy,
  orderByDirection,
  userArchivedFilters,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getCourseFilteredStudentQuery,
      variables: {
        id: courseId,
        filters,
        orderBy,
        orderByDirection,
        userArchivedFilters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getNodePostFromCache = ({ parentId, parentType, filters }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getNodePostQuery,
      variables: {
        id: parentId,
        type: parentType,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getParentJournalFeedFromCache = ({ filters, parentId }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getParentJournalFeedQuery,
      variables: {
        id: parentId,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCourseUnitFromCache = ({ filters, id, groupBy, uids }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getCourseUnitQuery,
      variables: {
        filters,
        id,
        groupBy,
        uids,
      },
    });
  } catch (e) {
    return {};
  }
  return !_.isNull(queryData) ? _.get(queryData, "node", {}) : {};
};

export const getOrganizationAcademicYearsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationAcademicYearsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getCommentsOfPostFromCache = ({ id, first, after, filters }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getCommentsOfPostQuery,
      variables: {
        id,
        first,
        after,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  return !_.isNull(queryData) ? _.get(queryData, "node", {}) : {};
};

export const getStudentActivityCriteriaSetsFromCache = ({
  organizationId,
  filters,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getStudentActivityCriteriaSetsQuery,
      variables: {
        organizationId,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  return !_.isNull(queryData) ? _.get(queryData, "node", {}) : {};
};

export const writeCommentOfPostInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `Message:${id}`,
      fragment: messageCommentOfPostFragment.message,
      fragmentName: "messageItem",
      data,
    });
  } catch (e) {
    console.error(e);
  }
};

export const readCommentOfPostFromCache = params => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `Message:${params.id}`,
      fragment: messageCommentOfPostFragment.message,
      fragmentName: "messageItem",
    });
  } catch (e) {
    console.warn(e);
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};
/*
export const writeCommentCountFragment = ({ postId, data }) => {
  try {
    client.writeFragment({
      id: `Post:${postId}`,
      fragment: PostCommentCountFragment.post,
      fragmentName: "postItem",
      data: data,
    });
  } catch (e) {}
};

export const readCommentCountFragment = ({ postId, data }) => {
  try {
    return client.readFragment({
      id: `Post:${postId}`,
      fragment: PostCommentCountFragment.post,
      fragmentName: "postItem",
    });
  } catch (e) {
    return 0;
  }
};
*/
export const writePostCommentFragment = ({
  postId,
  data,
  variables,
  fragment = PostCommentFragment.post,
}) => {
  try {
    client.writeFragment({
      id: `Post:${postId}`,
      fragment: fragment,
      fragmentName: "postItem",
      data: data,
      variables,
    });
  } catch (e) {
    console.error(e);
  }
};

export const readPostCommentFragment = ({
  postId,
  variables,
  fragment = PostCommentFragment.post,
}) => {
  try {
    return client.readFragment({
      id: `Post:${postId}`,
      fragment: fragment,
      fragmentName: "postItem",
      variables,
    });
  } catch (e) {
    return {};
  }
};

export const syncPostCommentsCache = ({
  postId,
  variables,
  fragment = PostCommentFragment,
}) => {
  const postDetails = readPostCommentFragment({
    postId,
    fragment: fragment.post,
  }); // read from post details
  const postCard = readPostCommentFragment({
    postId,
    variables,
    fragment: fragment.postWithVariables,
  }); // read from post card

  const updatedComments = _.get(postDetails, "comments");
  const currentComments = _.get(postCard, "comments");

  const newComments = {
    ...currentComments,
  };
  if (
    _.get(currentComments, "totalCount") !=
      _.get(updatedComments, "totalCount") ||
    _.get(currentComments, ["edges", 0, "node", "id"]) !=
      _.get(updatedComments, ["edges", 0, "node", "id"])
  ) {
    newComments.totalCount = _.get(updatedComments, "totalCount", 0);
    newComments.edges = _.compact([_.get(updatedComments, ["edges", 0])]);
  }
  writePostCommentFragment({
    postId,
    variables,
    data: {
      ...postCard,
      comments: newComments,
    },
    fragment: fragment.postWithVariables,
  }); // update to feed card cache
};

export const readActivityLikesAndCommentsFragment = ({ id }) => {
  try {
    return client.readFragment({
      id: `StudentActivity:${id}`,
      fragment: activityLikeCommentFragment.likesAndComments,
      fragmentName: "activityLikesAndComments",
    });
  } catch (e) {
    console.warn("Error in `readActivityLikesAndCommentsFragment`", e);
    return {};
  }
};

export const writeActivityLikesAndCommentsFragment = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `StudentActivity:${id}`,
      fragment: activityLikeCommentFragment.likesAndComments,
      fragmentName: "activityLikesAndComments",
      data,
    });
  } catch (e) {
    console.warn("Error in `writeActivityLikesAndCommentsFragment`", e);
  }
};

export const getPostsHavingTaggedGoalsQueryFromCache = ({
  courseId,
  postCount,
  postFilters,
  typeForPostQuery,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPostsHavingTaggedGoalsQuery,
      variables: {
        id: courseId,
        last: postCount,
        filters: postFilters,
        type: typeForPostQuery,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getStudentCoursesFromCache = ({
  studentId,
  filters,
  enabledModulesFilters,
  aclControlParameterFilter,
  postFilters,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getStudentCoursesQuery,
      variables: {
        studentId,
        filters,
        enabledModulesFilters,
        aclControlParameterFilter,
        ...(postFilters && { postFilters }),
      },
    });
  } catch (e) {
    return {};
  }
  return !_.isNull(queryData) ? _.get(queryData, "node", {}) : {};
};

export const getPlatformUserDetailsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getPlatformUserDetailsQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {}
};

export const getLearningCourseStandardsFromCache = variables => {
  try {
    const data = client.readQuery({
      query: getLearningCourseStandardsQuery,
      variables,
    });

    return _.get(data, "node", {});
  } catch (e) {
    return {};
  }
};

export const getCurriculumStandardsFromCache = variables => {
  try {
    const data = client.readQuery({
      query: getCurriculumStandardsQuery,
      variables,
    });

    return _.get(data, "node", {});
  } catch (e) {
    return {};
  }
};

export const getPlannerElementSetsOfCurriculumFromCache = ({
  id,
  elementTypes,
  type,
  filters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerElementSetsOfCurriculumQuery,
      variables: {
        id,
        elementTypes,
        type,
        filters,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return _.get(queryData, "node", {});
};

export const getAttachmentGroupsOfNodeForStudentAssignmentFromCache = ({
  id,
}) => {
  try {
    const queryData = client.readFragment({
      id: `StudentAssignmentSubmission:${id}`,
      fragment:
        attachmentGroupsNodeFragmentForStudentAssignment.studentAssignmentSubmissionAttachmentGroups,
      fragmentName: "studentAssignmentSubmissionAttachmentGroupsItem",
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const writeAttachmentGroupsOfNodeForStudentAssignmentInCache = ({
  id,
  data,
}) => {
  try {
    const queryData = client.writeFragment({
      id: `StudentAssignmentSubmission:${id}`,
      fragment:
        attachmentGroupsNodeFragmentForStudentAssignment.studentAssignmentSubmissionAttachmentGroups,
      fragmentName: "studentAssignmentSubmissionAttachmentGroupsItem",
      data,
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};
